<template>
  <div class="tab-item">
    <div class="substance-proprietes">
      <div
        v-if="actionProperties.length"
        class="substance-proprietes-section mb-4"
      >
        <div class="text-orange font-bold">Mode d'action</div>
        <div v-for="(action, index) in actionProperties" :key="index">
          <span class="text-gris-bordure">{{ action.text }} : </span>
          <span>{{ action.value }}</span>
        </div>
      </div>

      <div
        v-if="physicProperties.length"
        class="substance-proprietes-section mb-4"
      >
        <div class="text-orange font-bold">Propriétés physico-chimiques</div>
        <div v-for="(property, index) in physicProperties" :key="index">
          <span class="text-gris-bordure" v-html="property.text"> : </span>
          <span>{{ property.value }}</span>
        </div>
      </div>

      <div
        v-if="toxicProperties.length"
        class="substance-proprietes-section mb-4"
      >
        <div class="text-orange font-bold">
          Propriétés toxicologiques et écotoxicologiques
        </div>
        <div v-for="(property, index) in toxicProperties" :key="index">
          <span class="text-gris-bordure">{{ property.text }} : </span>
          <span>{{ property.value }}</span>
        </div>
      </div>

      <p v-if="!generalDisplay">
        Aucune donnée n'est actuellement disponible pour cette substance.
      </p>
    </div>
  </div>
</template>

<script>
export default {
  name: "SubstanceProperties",

  props: {
    substance: Object,
  },

  computed: {
    generalDisplay() {
      return (
        this.actionProperties?.length ||
        this.physicProperties?.length ||
        this.toxicProperties?.length
      );
    },

    actionProperties() {
      let activityProperties = this.activityActionProperties;

      return [
        {
          text: "Code RAC",
          value: this.substance.code_rac
            ?.map((x) => {
              return x.code + " / " + x.rac_type;
            })
            ?.join(", "),
        },
        ...activityProperties,
        {
          text: "Migration",
          value: this.substance.herbicide_migration,
        },
      ].filter((x) => x.value);
    },

    activityActionProperties() {
      const activity = this.substance.principal_activity;
      return this.actionPropertiesByActivity[activity] || [];
    },

    actionPropertiesByActivity() {
      return {
        herbicide: this.herbicideActionProperties,
        insecticide: this.insecticideActionProperties,
        fongicide: this.fongicideActionProperties,
      };
    },

    herbicideActionProperties() {
      return [
        {
          text: "Pénétration",
          value: this.substance.herbicide_penetration?.join(", "),
        },
        {
          text: "Action",
          value: this.substance.modes_action?.map((x) => x.name)?.join(", "),
        },
        {
          text: "Commentaire",
          value: this.substance.com_ma_cible_herbicide,
        },
      ].filter((x) => x.value);
    },

    insecticideActionProperties() {
      return [
        {
          text: "Comportement sur plante",
          value: this.substance.insecticide_plant_behavior,
        },
        {
          text: "Commentaire",
          value: this.substance.com_ma_plante_insecticide,
        },
        {
          text: "Mode d'action sur la cible",
          value: this.substance.insecticide_penetration?.join(", "),
        },
        {
          text: "Commentaire",
          value: this.substance.com_ma_cible_insecticide,
        },
      ].filter((x) => x.value);
    },

    fongicideActionProperties() {
      return [
        {
          text: "Comportement sur plante",
          value: this.substance.fungicide_plant_behavior?.join(", "),
        },
        {
          text: "Commentaire",
          value: this.substance.com_ma_plante_fongicide,
        },
        {
          text: "Comportement sur champignon",
          value: this.substance.fungicide_mushroom_behavior?.join(", "),
        },
      ].filter((x) => x.value);
    },

    physicProperties() {
      return [
        {
          text: "Masse moléculaire (g/mol) : ",
          value: this.substance.molecular_weight,
        },
        {
          text: "Aspect physique : ",
          value: this.substance.physical_aspect,
        },
        {
          text: "Solubilité (mg/l) : ",
          value: `${buildValueUnitText(
            this.substance.solubilite
          )}${buildTempText(this.substance.solubilite_temp)}${buildPHText(
            this.substance.solubilite_ph
          )}${buildCommentaireText(this.substance.solubilite_com)}`,
        },
        {
          text: "Kow : ",
          value: `${buildValueUnitText(this.substance.kow)}${buildTempText(
            this.substance.kow_temp
          )}${buildPHText(this.substance.kow_ph)}${buildCommentaireText(
            this.substance.kow_com
          )}`,
        },
        {
          text: "Log P : ",
          value: `${this.substance.logp || ""}${buildTempText(
            this.substance.logp_temp
          )}${buildPHText(this.substance.logp_ph)}${buildCommentaireText(
            this.substance.logp_com
          )}`,
        },
        {
          text: "Koc (ml/g) : ",
          value: `${this.substance.koc || ""}${buildCommentaireText(
            this.substance.koc_com
          )}`,
        },
        {
          text: "Kfoc : ",
          value: this.substance.kfoc,
        },
        {
          text: "Freundlich (1/n) : ",
          value: this.substance.freundlich,
        },
        {
          text: "Constante de Henry (Pa x m<sup>3</sup> x mol<sup>-1</sup>) : ",
          value: `${this.substance.cste_henry || ""}${buildTempText(
            this.substance.cste_henry_temp
          )}${buildCommentaireText(this.substance.cste_henry_com)}`,
        },
        {
          text: "Gus : ",
          value: `${this.substance.gus || ""}${buildCommentaireText(
            this.substance.gus_com
          )}`,
        },
        {
          text: "Pression de vapeur : ",
          value: `${buildValueUnitText(
            this.substance.pression_vapeur
          )}${buildTempText(this.substance.pression_vapeur_temp)}`,
        },
      ].filter((x) => x.value);
    },

    toxicProperties() {
      return [
        {
          text: "Dose journalière admissible - DJA (en mg/kg/jour)",
          value: `${
            this.substance.dja || ""
          }${nonEmpty`, ${this.substance.dja_source}`}${nonEmpty`, ${this.substance.dja_com}`}`,
        },
        {
          text: "Niveau Acceptable d’Exposition pour l’Opérateur - AOEL (en mg/kg/jour)",
          value: `${
            this.substance.aeol || ""
          }${nonEmpty`, ${this.substance.aeol_source}`}${nonEmpty`, ${this.substance.aeol_com}`}`,
        },
        {
          text: "Dose de Référence Aiguë – DRfA (en mg/kg/jour)",
          value: `${
            this.substance.drfa || ""
          }${nonEmpty`, ${this.substance.drfa_source}`}${nonEmpty`, ${this.substance.drfa_com}`}`,
        },
        {
          text: "Concentration sans effet prévisible sur l’environnement - PNEC (en µg/L)",
          value: this.substance.pnec,
        },
      ].filter((x) => x.value);
    },
  },
};

// Return empty string if any tag is falsy
function nonEmpty(statics, ...tags) {
  if (tags.some((x) => !x)) return "";
  var res = "";
  for (var i = 0; i < statics.length; i++) {
    res += statics[i] || "";
    res += tags[i] || "";
  }
  return res;
}

function buildValueUnitText(value) {
  if (!value) return "";
  return `${value}`;
}

function buildCommentaireText(commentaire) {
  if (!commentaire) return "";
  return ` ; ${commentaire}`;
}

function buildTempText(temp) {
  if (!temp) return "";
  return ` / à ${temp}°C`;
}

function buildPHText(ph) {
  if (!ph) return "";
  return ` / à pH ${ph}`;
}
</script>
